import { useState, useEffect, useCallback } from 'react'
import { db } from '../lib/supabase'

export function useSubscriptions(businessId = null) {
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true)
    const { data, error } = await db.getSubscriptions(businessId)
    if (error) {
      setError(error)
    } else {
      setSubscriptions(data)
    }
    setLoading(false)
  }, [businessId])

  useEffect(() => {
    fetchSubscriptions()
  }, [fetchSubscriptions])

  const createSubscription = async (subscription) => {
    const { data, error } = await db.createSubscription(subscription)
    if (!error && data) {
      setSubscriptions(prev => [...prev, data])
    }
    return { data, error }
  }

  const updateSubscription = async (id, updates) => {
    const { data, error } = await db.updateSubscription(id, updates)
    if (!error && data) {
      setSubscriptions(prev => prev.map(s => s.id === id ? data : s))
    }
    return { data, error }
  }

  const deleteSubscription = async (id) => {
    const { error } = await db.deleteSubscription(id)
    if (!error) {
      setSubscriptions(prev => prev.filter(s => s.id !== id))
    }
    return { error }
  }

  // Calculate monthly total
  const monthlyTotal = subscriptions.reduce((sum, sub) => {
    if (!sub.is_active) return sum
    const amount = parseFloat(sub.amount) || 0
    if (sub.frequency === 'yearly') return sum + (amount / 12)
    if (sub.frequency === 'quarterly') return sum + (amount / 3)
    return sum + amount
  }, 0)

  // Calculate yearly total
  const yearlyTotal = subscriptions.reduce((sum, sub) => {
    if (!sub.is_active) return sum
    const amount = parseFloat(sub.amount) || 0
    if (sub.frequency === 'monthly') return sum + (amount * 12)
    if (sub.frequency === 'quarterly') return sum + (amount * 4)
    return sum + amount
  }, 0)

  return {
    subscriptions,
    monthlyTotal,
    yearlyTotal,
    loading,
    error,
    refetch: fetchSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription,
  }
}

export default useSubscriptions
